import clsx from 'clsx'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { RtRole } from '../lib/rt-protocol'
import type { ChatEntry, UseChatResult } from './use-chat'

/**
 * Zoom-style chat (SLP UI v2 spec §聊天窗口).
 *
 * Pure presentational: all state lives in useChat — since G1 (v2.11) the hook
 * instance is App-level (RtConnectionProvider) so the transcript survives the
 * /join → /room transition.
 *
 * Two variants:
 *  - 'drawer' (room page): fixed 300px overlay above the right column —
 *    opening/closing NEVER squeezes the locked panel grid (v2.8 锁定铁律 #2).
 *    Collapsed → floating 💬 toggle with red unread dot.
 *  - 'inline' (join page, v2.11): resident panel inside the page's right
 *    column — always mounted, collapsible body, no floating toggle.
 */
export interface ChatPanelProps {
  chat: UseChatResult
  /** extra classes on the drawer root (layout tweaks by the integrator) */
  className?: string
  variant?: 'drawer' | 'inline'
}

// Chat role badge colors per spec: studio blue / director purple / engineer cyan / actor orange.
const ROLE_BADGE: Record<RtRole | 'unknown', string> = {
  studio: 'bg-[#29ABE2] text-white',
  director: 'bg-[#8B5CF6] text-white',
  engineer: 'bg-[#06B6D4] text-white',
  actor: 'bg-[#F97316] text-white',
  unknown: 'bg-gray-400 text-white',
}

function fmtClock(ts: number): string {
  const d = new Date(ts)
  const p = (n: number) => (n < 10 ? `0${n}` : String(n))
  return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

function ChatBubble({ m }: { m: ChatEntry }) {
  if (m.kind === 'system') {
    return (
      <div
        data-testid="rt-chat-msg"
        data-kind="system"
        className="px-2 py-0.5 text-center text-[11px] text-gray-400"
        title={fmtClock(m.ts)}
      >
        {m.text}
      </div>
    )
  }
  const mine = m.isLocal
  return (
    <div
      data-testid="rt-chat-msg"
      data-kind="chat"
      data-private={m.to ? 'true' : undefined}
      className={clsx('flex flex-col gap-0.5 px-2', mine ? 'items-end' : 'items-start')}
    >
      <div className="flex items-center gap-1 text-[11px] text-gray-500">
        <span className={clsx('rounded px-1 py-px text-[10px] font-semibold leading-3', ROLE_BADGE[m.role])}>
          {m.role}
        </span>
        <span className="font-medium">{mine ? `${m.name} (you)` : m.name}</span>
        {m.to ? (
          <span className="font-semibold text-red-500">
            (private{m.toName ? ` → ${m.toName}` : ''})
          </span>
        ) : null}
      </div>
      <div
        title={fmtClock(m.ts)}
        className={clsx(
          'max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-1.5 text-[12px] leading-snug',
          mine ? 'rounded-br-sm bg-[#29ABE2] text-white' : 'rounded-bl-sm bg-gray-100 text-gray-900',
        )}
      >
        {m.text}
      </div>
    </div>
  )
}

export function ChatPanel({ chat, className, variant = 'drawer' }: ChatPanelProps) {
  const [draft, setDraft] = useState('')
  const listRef = useRef<HTMLDivElement | null>(null)

  // Auto-scroll to the newest message.
  useEffect(() => {
    const el = listRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [chat.messages.length, chat.open])

  const doSend = useCallback(() => {
    if (!draft.trim()) return
    chat.send(draft, chat.target || undefined)
    setDraft('')
  }, [chat, draft])

  const onExport = useCallback(() => {
    const blob = new Blob([chat.exportText()], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `rt-chat-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`
    a.click()
    URL.revokeObjectURL(url)
  }, [chat])

  const body = (
    <>
      <div ref={listRef} className="flex flex-1 flex-col gap-2 overflow-y-auto py-2">
        {chat.messages.length === 0 ? (
          <div className="px-3 py-4 text-center text-[12px] text-gray-400">No messages yet</div>
        ) : (
          chat.messages.map((m) => <ChatBubble key={m.id} m={m} />)
        )}
      </div>

      <div className="border-t border-gray-200 p-2">
        <div className="mb-1 flex items-center gap-1 text-[11px] text-gray-600">
          <span className="shrink-0">Send to:</span>
          <select
            data-testid="rt-chat-to"
            value={chat.target}
            onChange={(e) => chat.setTarget(e.target.value)}
            className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-1 py-0.5 text-[11px]"
          >
            <option value="">Everyone</option>
            {chat.recipients.map((r) => (
              <option key={r.identity} value={r.identity}>
                {r.name} ({r.role})
              </option>
            ))}
          </select>
          {chat.target ? <span className="shrink-0 font-semibold text-red-500">(private)</span> : null}
        </div>
        <div className="flex items-end gap-1">
          <textarea
            data-testid="rt-chat-input"
            rows={2}
            value={draft}
            placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                doSend()
              }
            }}
            className="min-w-0 flex-1 resize-none rounded border border-gray-300 px-2 py-1 text-[12px] focus:border-[#29ABE2] focus:outline-none"
          />
          <button
            type="button"
            data-testid="rt-chat-send"
            onClick={doSend}
            disabled={!draft.trim()}
            className="rounded bg-[#29ABE2] px-3 py-1.5 text-[12px] font-semibold text-white hover:brightness-110 disabled:opacity-40"
          >
            Send
          </button>
        </div>
        <button
          type="button"
          onClick={onExport}
          disabled={chat.messages.length === 0}
          className="mt-1 text-[11px] text-[#29ABE2] underline-offset-2 hover:underline disabled:opacity-40"
        >
          Export .txt
        </button>
      </div>
    </>
  )

  if (variant === 'inline') {
    // v2.11 — resident chat column on the /join page. Always mounted; the
    // header button (rt-preflight-chat) collapses/expands the body only.
    return (
      <div
        data-testid="rt-chat-panel"
        data-variant="inline"
        className={clsx(
          'flex flex-col overflow-hidden rounded-md border border-[var(--slp-border)] bg-white shadow-sm',
          className,
        )}
      >
        <div className="flex h-[26px] shrink-0 items-center bg-[#29ABE2] px-2">
          <span className="flex-1 text-center text-[12px] font-semibold text-white">
            Chat — studio line
            {chat.unread > 0 && !chat.open ? (
              <span
                data-testid="rt-chat-unread"
                className="ml-2 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
              >
                {chat.unread > 99 ? '99+' : chat.unread}
              </span>
            ) : null}
          </span>
          <button
            type="button"
            data-testid="rt-preflight-chat"
            onClick={() => chat.setOpen(!chat.open)}
            aria-label={chat.open ? 'Collapse chat' : 'Expand chat'}
            className="rounded px-1.5 text-white hover:bg-white/20"
          >
            {chat.open ? '−' : '+'}
          </button>
        </div>
        {chat.open ? (
          body
        ) : (
          <div className="px-3 py-2 text-[11px] text-gray-400">
            Chat collapsed — the studio can still reach you here.
          </div>
        )}
      </div>
    )
  }

  if (!chat.open) {
    return (
      <button
        type="button"
        data-testid="rt-chat-toggle"
        onClick={() => chat.setOpen(true)}
        aria-label={`Open chat${chat.unread ? ` (${chat.unread} unread)` : ''}`}
        className="fixed bottom-12 right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full bg-[#29ABE2] text-xl text-white shadow-lg hover:brightness-110"
      >
        <span aria-hidden="true">💬</span>
        {chat.unread > 0 ? (
          <span
            data-testid="rt-chat-unread"
            className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white"
          >
            {chat.unread > 99 ? '99+' : chat.unread}
          </span>
        ) : null}
      </button>
    )
  }

  return (
    // v2.13: fixed 300px overlay — absolute layer above the right column, the
    // locked panel grid underneath never moves when this opens/closes.
    <aside
      data-testid="rt-chat-panel"
      className={clsx(
        'fixed bottom-[24px] right-0 top-0 z-40 flex w-[300px] flex-col border-l border-gray-200 bg-white shadow-xl',
        className,
      )}
    >
      {/* SLP-style bright blue title bar */}
      <div className="flex h-[26px] shrink-0 items-center bg-[#29ABE2] px-2">
        <span className="flex-1 text-center text-[12px] font-semibold text-white">Chat</span>
        <button
          type="button"
          data-testid="rt-chat-toggle"
          onClick={() => chat.setOpen(false)}
          aria-label="Collapse chat"
          className="rounded px-1.5 text-white hover:bg-white/20"
        >
          ×
        </button>
      </div>
      {body}
    </aside>
  )
}
